import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNowTick } from "@/lib/useNowTick";
import { Card } from "@/components/ui/card";
import UstaProjectRow from "@/components/UstaProjectRow";

const STATUS = {
  active: { label: "Aktif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  completed: { label: "Tamamlandı", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  cancelled: { label: "İptal", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};

export default function ProjectsList() {
  useNowTick(60);
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stages, setStages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [year, setYear] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", name: "", status: "active", start_date: "", end_date: "", notes: "" });

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    if (customerId) params.customer_id = customerId;
    const { data } = await api.get("/projects", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status, customerId]);
  useEffect(() => { if (isAdmin) api.get("/customers").then(r => setCustomers(r.data)); }, [isAdmin]);
  useEffect(() => { if (!isAdmin) api.get("/settings").then(r => setStages(r.data.manufacturing_stages || [])); }, [isAdmin]);
  useEffect(() => { if (!isAdmin) api.get("/product-categories").then(r => setCategories(r.data)).catch(() => setCategories([])); }, [isAdmin]);

  // Derive available years from item start_dates
  const availableYears = Array.from(new Set(
    items.map(p => p.start_date ? p.start_date.slice(0, 4) : null).filter(Boolean)
  )).sort((a, b) => b.localeCompare(a));

  // Apply year filter + sort by start_date asc (fallback alphabetic for missing dates)
  const displayedItems = items
    .filter(p => {
      if (!year) return true;
      if (!p.start_date) return false;
      return p.start_date.slice(0, 4) === year;
    })
    .sort((a, b) => {
      const aHas = !!a.start_date;
      const bHas = !!b.start_date;
      if (aHas && bHas) return a.start_date.localeCompare(b.start_date);
      if (aHas) return -1;
      if (bHas) return 1;
      return (a.name || "").localeCompare(b.name || "", "tr");
    });

  const save = async () => {
    if (!form.customer_id) { toast.error("Müşteri seçin"); return; }
    if (!form.name) { toast.error("Proje adı zorunlu"); return; }
    try {
      const body = {
        ...form,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      };
      const r = await api.post("/projects", body);
      toast.success("Proje oluşturuldu");
      setOpen(false);
      setForm({ customer_id: "", name: "", status: "active", start_date: "", end_date: "", notes: "" });
      nav(`/projeler/${r.data.id}`);
    } catch (e) { toast.error("Kaydedilemedi"); }
  };

  return (
    <div className="space-y-6" data-testid="projects-list-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500">Üretim</div>
          <h1 className="text-3xl mt-1">Projeler</h1>
        </div>
        <Button data-testid="new-project-button" onClick={() => setOpen(true)} className="bg-zinc-900 hover:bg-zinc-800" style={{display: isAdmin ? undefined : "none"}}>
          <Plus size={16} className="mr-1" />Yeni Proje
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="w-72">
          <Label>Ara</Label>
          <Input data-testid="project-search-input" placeholder="Proje adı..." value={q} onChange={(e)=>setQ(e.target.value)} />
        </div>
        {isAdmin && (
          <div className="w-52">
            <Label>Durum</Label>
            <Select value={status || "all"} onValueChange={(v)=>setStatus(v==="all"?"":v)}>
              <SelectTrigger data-testid="project-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="completed">Tamamlandı</SelectItem>
                <SelectItem value="cancelled">İptal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {isAdmin && (
          <div className="w-72">
            <Label>Müşteri</Label>
            <Select value={customerId || "all"} onValueChange={(v)=>setCustomerId(v==="all"?"":v)}>
              <SelectTrigger><SelectValue placeholder="Tümü" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="w-40">
          <Label>Yıl</Label>
          <Select value={year || "all"} onValueChange={(v)=>setYear(v==="all"?"":v)}>
            <SelectTrigger data-testid="project-year-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {availableYears.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!isAdmin ? (
        <div className="space-y-3" data-testid="usta-projects-container">
          {displayedItems.length === 0 && (
            <Card className="p-8 border border-zinc-200 shadow-none text-center text-zinc-500 text-sm">
              Aktif proje bulunmuyor.
            </Card>
          )}
          {displayedItems.map(p => <UstaProjectRow key={p.id} project={p} stages={stages} categories={categories} />)}
        </div>
      ) : (
        <div className="bg-white rounded-md border border-zinc-200 overflow-hidden">
          <table className="acc">
            <thead>
              <tr><th>Ad</th><th>Müşteri</th><th>Başlangıç</th><th>Bitiş</th><th>Durum</th></tr>
            </thead>
            <tbody>
              {displayedItems.length === 0 && <tr><td colSpan={5} className="text-center text-zinc-500 py-8">Henüz proje yok</td></tr>}
              {displayedItems.map(p => (
                <tr key={p.id} data-testid={`project-row-${p.id}`} onClick={()=>nav(`/projeler/${p.id}`)} className="cursor-pointer">
                  <td className="font-medium flex items-center gap-2"><FolderKanban size={14} className="text-zinc-400"/>{p.name}</td>
                  <td><Link onClick={(e)=>e.stopPropagation()} to={`/musteriler/${p.customer_id}`} className="text-zinc-600 hover:underline">{p.customer_name}</Link></td>
                  <td className="text-zinc-600">{p.start_date?.slice(0,10) || "-"}</td>
                  <td className="text-zinc-600">{p.end_date?.slice(0,10) || "-"}</td>
                  <td><span className={`inline-block px-2 py-0.5 rounded text-xs border ${STATUS[p.status]?.cls || ""}`}>{STATUS[p.status]?.label || p.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="project-form-dialog">
          <DialogHeader><DialogTitle>Yeni Proje</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Müşteri</Label>
              <Select value={form.customer_id} onValueChange={(v)=>setForm({...form, customer_id: v})}>
                <SelectTrigger data-testid="project-customer-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Proje Adı</Label><Input data-testid="project-name-input" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} /></div>
            <div><Label>Başlangıç</Label><Input type="date" value={form.start_date} onChange={(e)=>setForm({...form, start_date: e.target.value})} /></div>
            <div><Label>Bitiş</Label><Input type="date" value={form.end_date} onChange={(e)=>setForm({...form, end_date: e.target.value})} /></div>
            <div className="col-span-2">
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v)=>setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="completed">Tamamlandı</SelectItem>
                  <SelectItem value="cancelled">İptal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Notlar</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>İptal</Button>
            <Button data-testid="project-save-button" onClick={save} className="bg-zinc-900 hover:bg-zinc-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
